import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Box,
  Button,
  ListItemText,
  OutlinedInput,
  Chip,
  type SelectChangeEvent,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { WORKFLOW_TYPES, TYPE_LABELS } from '@/resources/workflowTypeMeta';

interface WorkflowFiltersProps {
  filters?: {
    status: string[];
    workflowType: string[];
    search: string;
  };
  onChange?: (filters: { status?: string[]; workflowType?: string[]; search?: string }) => void;
  workflowTypes?: string[];
}

const STATUS_OPTIONS = ['active', 'paused', 'completed', 'cancelled'];

export function WorkflowFilters({
  filters = { status: [], workflowType: [], search: '' },
  onChange,
  workflowTypes = WORKFLOW_TYPES,
}: WorkflowFiltersProps) {
  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    onChange?.({ status: value });
  };

  const handleWorkflowTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    onChange?.({ workflowType: value });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.({ search: event.target.value });
  };

  const handleClearFilters = () => {
    onChange?.({ status: [], workflowType: [], search: '' });
  };

  const hasActiveFilters =
    (filters.status && filters.status.length > 0) ||
    (filters.workflowType && filters.workflowType.length > 0) ||
    (filters.search && filters.search.trim() !== '');

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <TextField
        label="Search"
        placeholder="Employee or entity name"
        value={filters.search || ''}
        onChange={handleSearchChange}
        size="small"
        sx={{ minWidth: 200 }}
      />

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select
          multiple
          value={filters.status || []}
          onChange={handleStatusChange}
          input={<OutlinedInput label="Status" />}
          renderValue={(selected) =>
            (selected as string[])
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
              .join(', ')
          }
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        >
          {STATUS_OPTIONS.map((status) => (
            <MenuItem key={status} value={status}>
              <Checkbox checked={(filters.status || []).indexOf(status) > -1} />
              <ListItemText primary={status.charAt(0).toUpperCase() + status.slice(1)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Workflow Type</InputLabel>
        <Select
          multiple
          value={filters.workflowType || []}
          onChange={handleWorkflowTypeChange}
          input={<OutlinedInput label="Workflow Type" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((value) => (
                <Chip
                  key={value}
                  label={TYPE_LABELS[value] || value}
                  size="small"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          )}
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        >
          {workflowTypes.map((type) => (
            <MenuItem key={type} value={type}>
              <Checkbox checked={(filters.workflowType || []).indexOf(type) > -1} />
              <ListItemText
                primary={TYPE_LABELS[type] || type}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActiveFilters && (
        <Button
          variant="text"
          size="small"
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
          sx={{ color: 'text.secondary' }}
        >
          Clear Filters
        </Button>
      )}
    </Box>
  );
}
